import { useEffect, useState } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { format } from 'date-fns';
import { RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { useMarkPaymentAsPaid } from '@/hooks/finance/useReceivables';
import { useBankAccounts } from '@/hooks/finance';
import { useQueryClient } from '@tanstack/react-query';

const RECEIVABLES_MANUAL_METHOD_OPTIONS = [
  { value: 'pix', label: 'PIX' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'ach', label: 'ACH' },
  { value: 'wire', label: 'Wire' },
  { value: 'check', label: 'Cheque' },
  { value: 'cash', label: 'Dinheiro' },
] as const;

interface RegisterPaymentInlineModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: {
    id: string;
    amount: number;
    paid_amount: number;
    balance: number;
    currency?: string | null;
    description?: string | null;
    business_unit?: string | null;
  } | null;
  clientName?: string | null;
  dealId?: string | null;
  onSuccess?: () => void;
}

export default function RegisterPaymentInlineModal({
  open,
  onOpenChange,
  payment,
  clientName,
  dealId,
  onSuccess,
}: RegisterPaymentInlineModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const markAsPaid = useMarkPaymentAsPaid();
  const { data: bankAccounts = [] } = useBankAccounts('all');

  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState(safeFormat(new Date(), 'yyyy-MM-dd'));
  const [paymentMethod, setPaymentMethod] = useState('');
  const [payBankAccountId, setPayBankAccountId] = useState('');
  const [payNotes, setPayNotes] = useState('');

  // Reset on open
  useEffect(() => {
    if (open && payment) {
      setPayAmount(String(payment.balance ?? payment.amount ?? 0));
      setPayDate(safeFormat(new Date(), 'yyyy-MM-dd'));
      setPaymentMethod('');
      setPayBankAccountId('');
      setPayNotes('');
    }
  }, [open, payment?.id]);

  const handleSubmit = async () => {
    if (!payment) return;
    if (!payAmount || !payDate) {
      toast({
        variant: 'destructive',
        title: 'Campos obrigatórios',
        description: 'Preencha valor e data do pagamento.',
      });
      return;
    }
    if (!paymentMethod) {
      toast({
        variant: 'destructive',
        title: 'Método de pagamento',
        description: 'Selecione o método de pagamento.',
      });
      return;
    }
    const requiresBank = paymentMethod !== 'cash';
    if (requiresBank && !payBankAccountId) {
      toast({
        variant: 'destructive',
        title: 'Conta bancária',
        description: 'Selecione a conta bancária do recebimento.',
      });
      return;
    }

    try {
      await markAsPaid.mutateAsync({
        paymentId: payment.id,
        paidAmount: parseFloat(payAmount),
        paidAt: payDate,
        paymentMethod,
        bankAccountId: payBankAccountId || undefined,
        notes: payNotes || undefined,
      });

      // Invalidate deal-scoped queries so the PaymentsTab refreshes immediately
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      if (dealId) {
        queryClient.invalidateQueries({ queryKey: ['case-payment-status', dealId] });
        queryClient.invalidateQueries({ queryKey: ['deal-metrics', dealId] });
      }

      toast({
        title: 'Pagamento registrado',
        description: 'Baixa realizada com sucesso.',
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao registrar pagamento',
        description: String(error?.message || 'Tente novamente.'),
      });
    }
  };

  const formatMoney = (value: number, currency: string = 'USD') => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return `${currency} ${value.toFixed(2)}`;
    }
  };

  const currency = (payment?.currency as string) || 'USD';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registrar Pagamento</DialogTitle>
          <DialogDescription>
            {clientName || 'Cliente'}{payment?.description ? ` — ${payment.description}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>Saldo em aberto</Label>
            <div className="text-lg font-medium">
              {formatMoney(payment?.balance || 0, currency)}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Valor Pago *</Label>
            <Input
              type="number"
              step="0.01"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Deixe o saldo total para baixa integral ou ajuste para pagamento parcial.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Data do Pagamento *</Label>
            <Input
              type="date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Método de Pagamento *</Label>
            <Select
              value={paymentMethod}
              onValueChange={(v) => {
                setPaymentMethod(v);
                const currencyFilter =
                  v === 'pix' ? 'BRL' : ['zelle', 'ach', 'wire'].includes(v) ? 'USD' : null;
                if (currencyFilter && payBankAccountId) {
                  const selected = bankAccounts.find((a) => a.id === payBankAccountId);
                  if (selected && selected.currency !== currencyFilter) setPayBankAccountId('');
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                {RECEIVABLES_MANUAL_METHOD_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>
              Conta Bancária
              {paymentMethod !== 'cash' ? ' *' : ' (opcional para Dinheiro)'}
            </Label>
            <Select value={payBankAccountId} onValueChange={setPayBankAccountId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                {bankAccounts
                  .filter((acc) => {
                    if (paymentMethod === 'pix') return acc.currency === 'BRL';
                    if (['zelle', 'ach', 'wire'].includes(paymentMethod)) return acc.currency === 'USD';
                    if (['cash', 'check'].includes(paymentMethod)) return true;
                    if (payment?.business_unit && acc.business_unit !== payment.business_unit) return false;
                    return true;
                  })
                  .map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.name} ({acc.currency})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea
              value={payNotes}
              onChange={(e) => setPayNotes(e.target.value)}
              placeholder="Notas sobre o pagamento..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              markAsPaid.isPending ||
              !paymentMethod ||
              (paymentMethod !== 'cash' && !payBankAccountId)
            }
          >
            {markAsPaid.isPending && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
            Confirmar Pagamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
